import PropTypes from 'prop-types'
import React, {forwardRef, useMemo, useState} from 'react'
import {Box, useRecipe} from '@chakra-ui/react3'
import {useMergeRefs} from '@chakra-ui/react-use-merge-refs'
import useCmsLazyImgLoad from '../../../components/cms-content/hooks/use-cms-lazy-img-load'
import styledContentRecipe from './index.recipe'

/**
 * A component that renders plain html content. Provides default styling and
 * allows overriding the styling via regular chakra styling props.
 * Support also textStyle prop to apply predefined text styles.
 * Also supports lazy loading of images.
 */
const StyledContent = forwardRef(({content, ...props}, ref) => {
    const recipe = useRecipe({recipe: styledContentRecipe})
    const linkRecipe = useRecipe({key: 'link'})
    const buttonRecipe = useRecipe({key: 'button'})
    const styles = useMemo(() => {
        const baseStyles = recipe()
        const leapLinkStyles = linkRecipe({
            variant: 'secondary',
            underline: true,
            size: 'small'
        })
        const leapButtonStyles = buttonRecipe({
            variant: 'primaryFilled',
            size: 'md',
            width: 'hugContent'
        })

        return {
            ...baseStyles,
            '& a, & .sk-link': {
                ...leapLinkStyles,
                // CMS HTML links may contain plain text instead of a wrapped <span>,
                // so apply the style on the link itself.
                borderBottomWidth: '1px',
                '& span': {
                    borderBottomWidth: 0
                }
            },
            '& button, & .sk-button': {
                ...leapButtonStyles,
                // CMS HTML buttons may contain plain text instead of a wrapped <span>,
                // so apply the text style on the button itself.
                textStyle: 'heading.small'
            }
        }
    }, [recipe, linkRecipe, buttonRecipe])

    const [root, setRoot] = useState()
    const mergedRef = useMergeRefs(ref, setRoot)

    useCmsLazyImgLoad(root)

    return (
        <Box
            {...props}
            css={[styles, props.css]}
            className={`styled-content ${props.className}`}
            ref={mergedRef}
            dangerouslySetInnerHTML={{__html: content}}
        />
    )
})

if (process.env.NODE_ENV !== 'production') {
    StyledContent.displayName = 'StyledContent'
}

StyledContent.propTypes = {
    className: PropTypes.string,
    content: PropTypes.string,
    css: PropTypes.oneOfType([PropTypes.object, PropTypes.array])
}

export default StyledContent
